import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PlayIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeIcon from '@mui/icons-material/VolumeUp';
import { useTranslation } from 'react-i18next';

const SEGUNDOS_POR_MINUTO = 60;
const PASO_PROGRESO = 1;
const VOLUMEN_INICIAL = 1;
const PASO_VOLUMEN = 0.05;

const formatoTiempo = (segundos) => {
  const total = Number.isFinite(segundos) ? Math.floor(segundos) : 0;
  return `${Math.floor(total / SEGUNDOS_POR_MINUTO)}:${String(total % SEGUNDOS_POR_MINUTO).padStart(2, '0')}`;
};

const estiloRango = (color) => ({
  accentColor: color, cursor: 'pointer', height: '1.2rem', m: 0,
  '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: '2px' }
});

export const ETIQUETAS_MUSICA = {
  reproductor: 'ficha.musica_reproductor', progreso: 'ficha.musica_progreso', error: 'ficha.musica_error'
};
export const ETIQUETAS_AUDIO = {
  reproductor: 'ficha.audio_reproductor', progreso: 'ficha.audio_progreso', error: 'ficha.audio_error'
};

const ReproductorAudio = ({ titulo, artista, url, color, etiquetas }) => {
  const { t } = useTranslation();
  const audio = useRef(null);
  const [sonando, setSonando] = useState(false);
  const [tiempo, setTiempo] = useState(0);
  const [duracion, setDuracion] = useState(0);
  const [volumen, setVolumen] = useState(VOLUMEN_INICIAL);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    setSonando(false);
    setTiempo(0);
    setFallo(false);
  }, [url]);

  const alternar = () => {
    const elemento = audio.current;
    if (!elemento) return;
    if (elemento.paused) elemento.play().catch(() => setFallo(true));
    else elemento.pause();
  };

  const mover = (event) => {
    const valor = Number(event.target.value);
    audio.current.currentTime = valor;
    setTiempo(valor);
  };

  const ajustarVolumen = (event) => {
    const valor = Number(event.target.value);
    audio.current.volume = valor;
    setVolumen(valor);
  };

  return (
    <Box className="no-imprimir" role="group" aria-label={t(etiquetas.reproductor)} sx={{
      mt: 2, p: 2, border: '1px solid var(--linea)', bgcolor: 'var(--superficie)'
    }}>
      <audio
        ref={audio}
        src={url}
        preload="metadata"
        onPlay={() => setSonando(true)}
        onPause={() => setSonando(false)}
        onEnded={() => setSonando(false)}
        onTimeUpdate={(event) => setTiempo(event.target.currentTime)}
        onLoadedMetadata={(event) => setDuracion(event.target.duration)}
        onError={() => setFallo(true)}
      />
      <Typography component="p" sx={{ color: 'var(--texto)', fontSize: '1rem', lineHeight: 1.3 }}>
        {titulo}
      </Typography>
      {artista && (
        <Typography component="p" sx={{
          color: 'var(--texto-tenue)', fontFamily: 'var(--fuente-titulo)', fontSize: '.8rem', letterSpacing: '.04em'
        }}>
          {artista}
        </Typography>
      )}
      {fallo && (
        <Typography role="alert" sx={{ color: 'var(--texto-tenue)', fontSize: '.85rem', mt: 1 }}>
          {t(etiquetas.error)}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
        <Box
          component="button" type="button" onClick={alternar}
          aria-label={sonando ? t('ficha.musica_pausar') : t('ficha.musica_reproducir')}
          sx={{
            display: 'inline-flex', cursor: 'pointer', p: .7, bgcolor: 'transparent', color: 'var(--texto)',
            border: `1px solid ${color}`, borderRadius: '50%',
            '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: '2px' }
          }}
        >
          {sonando ? <PauseIcon /> : <PlayIcon />}
        </Box>
        <Typography component="span" sx={{ color: 'var(--texto-tenue)', fontSize: '.78rem', minWidth: '2.6rem' }}>
          {formatoTiempo(tiempo)}
        </Typography>
        <Box
          component="input" type="range" min={0} max={duracion || 0} step={PASO_PROGRESO} value={tiempo}
          onChange={mover} aria-label={t(etiquetas.progreso)}
          aria-valuetext={`${formatoTiempo(tiempo)} / ${formatoTiempo(duracion)}`}
          sx={{ ...estiloRango(color), flex: '1 1 8rem' }}
        />
        <Typography component="span" sx={{ color: 'var(--texto-tenue)', fontSize: '.78rem', minWidth: '2.6rem' }}>
          {formatoTiempo(duracion)}
        </Typography>
        <VolumeIcon aria-hidden="true" sx={{ color: 'var(--texto-tenue)', fontSize: '1.1rem' }} />
        <Box
          component="input" type="range" min={0} max={1} step={PASO_VOLUMEN} value={volumen}
          onChange={ajustarVolumen} aria-label={t('ficha.musica_volumen')}
          sx={{ ...estiloRango(color), width: '5rem' }}
        />
      </Box>
    </Box>
  );
};

export default ReproductorAudio;
